import axios from 'axios';

import { getActiveToken } from './getActiveToken';
import {
  adoptValidatedToken,
  getActiveSession,
  getTokenCandidate,
  setActiveSessionStatus,
} from './tokenManager';
import {
  createReauthenticationRequiredError,
  isThreadsAuthError,
} from './authErrors';

/**
 * Threads Graph API 범용 프록시 Cloud Function URL
 * graph.threads.net 의 데이터 엔드포인트는 브라우저 CORS 응답 헤더를 내려주지 않아
 * 직접 호출이 차단된다(요청은 200 으로 도달하지만 응답을 읽을 수 없음).
 * 서버(threadsApiProxy)를 경유해 우회한다. (conversation 의 fetchConversations 와 동일 패턴)
 */
export const THREADS_API_PROXY_URL =
  'https://us-central1-my-threads-insight.cloudfunctions.net/threadsApiProxy';

let authRecoveryPromise = null;

const removeEmbeddedAccessToken = (pathOrUrl) => {
  if (!/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;

  try {
    const parsed = new URL(pathOrUrl);
    if (parsed.protocol === 'https:' && parsed.hostname === 'graph.threads.net') {
      parsed.searchParams.delete('access_token');
      return parsed.toString();
    }
  } catch (_) {}

  return pathOrUrl;
};

const getConfiguredToken = (config = {}) =>
  config.params?.access_token || getActiveToken();

const requestThroughProxy = async (pathOrUrl, config = {}, forcedToken = null) => {
  const sanitizedTarget = removeEmbeddedAccessToken(pathOrUrl);
  const isAbsolute = /^https?:\/\//.test(sanitizedTarget);
  const params = config.params || {};
  const { access_token: tokenFromParams, ...restParams } = params;
  const token = forcedToken || tokenFromParams || getActiveToken();

  if (!token) {
    const error = new Error('No active Threads token');
    error.code = 'NO_ACTIVE_TOKEN';
    throw error;
  }

  const body = isAbsolute
    ? { token, url: sanitizedTarget, params: restParams }
    : { token, path: sanitizedTarget, params: restParams };

  const proxyResponse = await axios.post(THREADS_API_PROXY_URL, body, {
    timeout: config.timeout || 30000,
  });

  return { data: proxyResponse.data?.data, status: proxyResponse.status };
};

const createTransientRecoveryError = (cause) => {
  const error = new Error('Could not check the latest Threads session');
  error.name = 'AuthRecoveryTransientError';
  error.code = 'AUTH_RECOVERY_TRANSIENT';
  error.cause = cause;
  return error;
};

const recoverActiveToken = async (failedToken, failedGeneration) => {
  const currentSession = getActiveSession();

  // Another request (or tab) already adopted a newer token while this request
  // was in flight. Reuse it instead of starting another Firestore read.
  if (
    currentSession.token &&
    currentSession.token !== failedToken &&
    currentSession.generation !== failedGeneration
  ) {
    return currentSession.token;
  }

  if (authRecoveryPromise) return authRecoveryPromise;

  authRecoveryPromise = (async () => {
    const userID = currentSession.userID || localStorage.getItem('userID');
    if (!userID) {
      setActiveSessionStatus('reauth_required');
      throw createReauthenticationRequiredError();
    }

    setActiveSessionStatus('recovering');

    let candidate;
    try {
      candidate = await getTokenCandidate(userID);
    } catch (error) {
      if (
        error?.code === 'TOKEN_CANDIDATE_NOT_FOUND' ||
        error?.code === 'TOKEN_CANDIDATE_INVALID' ||
        error?.code === 'TOKEN_CANDIDATE_EXPIRED'
      ) {
        setActiveSessionStatus('reauth_required');
        throw createReauthenticationRequiredError(error);
      }
      setActiveSessionStatus('transient_error');
      throw createTransientRecoveryError(error);
    }

    if (!candidate?.token || candidate.token === failedToken) {
      setActiveSessionStatus('reauth_required');
      throw createReauthenticationRequiredError();
    }

    try {
      const profileResponse = await requestThroughProxy(
        'me',
        { params: { fields: 'id' }, timeout: 15000 },
        candidate.token
      );
      const profileID = profileResponse.data?.id;

      if (!profileID || String(profileID) !== String(userID)) {
        setActiveSessionStatus('reauth_required');
        throw createReauthenticationRequiredError();
      }

      adoptValidatedToken({ userID, token: candidate.token });
      console.warn('[graphGet] Threads session recovered with the latest stored token');
      return candidate.token;
    } catch (error) {
      if (error?.code === 'REAUTH_REQUIRED' || isThreadsAuthError(error)) {
        setActiveSessionStatus('reauth_required');
        throw createReauthenticationRequiredError(error);
      }

      setActiveSessionStatus('transient_error');
      throw createTransientRecoveryError(error);
    }
  })();

  try {
    return await authRecoveryPromise;
  } finally {
    authRecoveryPromise = null;
  }
};

/**
 * Graph API GET 을 프록시 Cloud Function 을 통해 수행하고 axios 와 동일한 형태로 반환
 * @param {string} pathOrUrl - 상대 경로(예: 'me/threads') 혹은 절대 URL(페이지네이션 after 등)
 * @param {object} config - axios 스타일 config ({ params, timeout })
 * @returns {Promise<{data: any, status: number}>} axios 호환 응답 (data = 원본 Graph 응답 본문)
 */
export const graphProxyGet = async (pathOrUrl, config = {}) => {
  return requestThroughProxy(pathOrUrl, config);
};

/**
 * Graph API GET with optional 5xx retry (프록시 경유)
 * @param {string} pathOrUrl - 상대 경로 혹은 절대 URL
 * @param {object} config - axios config
 * @param {object} options - { retryOn5xx?: boolean }
 */
export const graphGet = async (pathOrUrl, config = {}, options = {}) => {
  const { retryOn5xx = true, recoverAuth = true } = options;
  const maxAttempts = retryOn5xx ? 2 : 1;
  const initialSession = getActiveSession();
  const requestToken = getConfiguredToken(config);

  let lastError = null;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await requestThroughProxy(pathOrUrl, config, requestToken);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const message = error?.response?.data?.error || error?.message;
      console.warn('[graphGet] request failed', { pathOrUrl, status, message });

      if (recoverAuth && isThreadsAuthError(error)) {
        const recoveredToken = await recoverActiveToken(
          requestToken,
          initialSession.generation
        );

        try {
          // Threads Graph reads are GET operations behind this POST proxy, so
          // replaying the original read once is safe.
          return await requestThroughProxy(pathOrUrl, config, recoveredToken);
        } catch (retryError) {
          if (isThreadsAuthError(retryError)) {
            setActiveSessionStatus('reauth_required');
            throw createReauthenticationRequiredError(retryError);
          }
          throw retryError;
        }
      }

      const canRetry =
        retryOn5xx && status && status >= 500 && status < 600 && attempt < maxAttempts - 1;
      if (!canRetry) {
        throw error;
      }
    }
  }

  throw lastError;
};
